import React, { useState } from "react";
import ProteinContent from "../ProteinIntakeCalculator/components/ProteinContent/ProteinContent";
import ProteinFAQ from "../ProteinIntakeCalculator/components/ProteinFAQ/ProteinFAQ";
import "./AdvancedProteinIntakeCalculator.css";

const getProteinSources = (diet) => {
  if (diet === "Vegan") {
    return ["Lentils", "Tofu", "Chickpeas", "Quinoa", "Pea Protein"];
  } else if (diet === "Vegetarian") {
    return ["Greek Yogurt", "Eggs", "Paneer", "Milk", "Cottage Cheese"];
  } else if (diet === "Omnivore") {
    return ["Chicken Breast", "Eggs", "Whey Protein", "Fish", "Beef"];
  }
  return [];
};

const AdvancedProteinIntakeCalculator = () => {
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [activityLevel, setActivityLevel] = useState("");
  const [goal, setGoal] = useState("");
  const [timing, setTiming] = useState("daily");
  const [diet, setDiet] = useState("");
  const [results, setResults] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Convert weight to kg if necessary
  const convertToKilograms = (weight, unit) => {
    return unit === "kg" ? parseFloat(weight) : parseFloat(weight) * 0.453592;
  };

  // Protein calculation logic
  const calculateProtein = () => {
    const weightInKg = convertToKilograms(weight, weightUnit);

    if (isNaN(weightInKg) || weightInKg <= 0) {
      setErrorMessage("Please enter a valid weight.");
      return;
    }
    if (!activityLevel || !goal || !diet) {
      setErrorMessage("Please fill out all fields.");
      return;
    }

    let multiplier = 1.2; // Default for moderate activity and maintenance
    if (activityLevel === "Sedentary") multiplier = 0.8;
    if (activityLevel === "Intense") multiplier = 1.6;
    if (goal === "Muscle Gain") multiplier += 0.5;

    const dailyProtein = weightInKg * multiplier;
    const postWorkoutProtein =
      timing === "post" ? dailyProtein * 0.7 : dailyProtein;

    const recommendations = getProteinSources(diet);

    setResults({
      dailyProtein: dailyProtein.toFixed(2),
      postWorkoutProtein: postWorkoutProtein.toFixed(2),
      recommendations,
    });

    setErrorMessage("");
  };

  // Get recommended protein sources based on dietary preference const getProteinSources = (diet) => { if (diet === "Vegan") { return ["Lentils", "Tofu", "Chickpeas", "Quinoa", "Pea Protein"]; } else if (diet === "Vegetarian") { return ["Greek Yogurt", "Eggs", "Paneer", "Milk", "Cottage Cheese"]; } else if (diet === "Omnivore") { return ["Chicken Breast", "Eggs", "Whey Protein", "Fish", "Beef"]; } return []; };

  const resetCalculator = () => {
    setWeight("");
    setWeightUnit("kg");
    setActivityLevel("");
    setGoal("");
    setTiming("daily");
    setDiet("");
    setResults(null);
    setErrorMessage("");
  };

  return (
    <div className="protein-calculator-container">
      {" "}
      <div className="calculator-wrapper">
        <h2>Advanced Protein Intake Calculator</h2>{" "}
        <div className="form-container">
          {errorMessage && <p className="error-message">{errorMessage}</p>}

          {/* Weight Input */}
          <div className="input-container">
            <label htmlFor="weight">Weight:</label>
            <div className="weight-input-group">
              <input
                type="number"
                id="weight"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Enter weight"
              />
              <select
                id="weight-unit"
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
              >
                <option value="kg">kg</option>
                <option value="lbs">lbs</option>
              </select>
            </div>
          </div>

          {/* Activity Level */}
          <div className="input-container">
            <label htmlFor="activity-level">Activity Level:</label>
            <select
              id="activity-level"
              value={activityLevel}
              onChange={(e) => setActivityLevel(e.target.value)}
            >
              <option value="" disabled>
                -- Select Activity Level --
              </option>
              <option value="Sedentary">Sedentary</option>
              <option value="Moderate">Moderate</option>
              <option value="Intense">Intense</option>
            </select>
          </div>

          {/* Fitness Goal */}
          <div className="input-container">
            <label htmlFor="goal">Fitness Goal:</label>
            <select
              id="goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            >
              <option value="" disabled>
                -- Select Goal --
              </option>
              <option value="Weight Loss">Weight Loss</option>
              <option value="Muscle Gain">Muscle Gain</option>
              <option value="Maintenance">Maintenance</option>
            </select>
          </div>

          {/* Workout Timing */}
          <div className="input-container">
            <label>Workout Timing:</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="timing"
                  value="daily"
                  checked={timing === "daily"}
                  onChange={(e) => setTiming(e.target.value)}
                />
                Daily Requirement
              </label>
              <label>
                <input
                  type="radio"
                  name="timing"
                  value="post"
                  checked={timing === "post"}
                  onChange={(e) => setTiming(e.target.value)}
                />
                Post-Workout
              </label>
            </div>
          </div>

          {/* Dietary Preference */}
          <div className="input-container">
            <label htmlFor="diet">Dietary Preference:</label>
            <select
              id="diet"
              value={diet}
              onChange={(e) => setDiet(e.target.value)}
            >
              <option value="" disabled>
                -- Select Dietary Preference --
              </option>
              <option value="Vegan">Vegan</option>
              <option value="Vegetarian">Vegetarian</option>
              <option value="Omnivore">Omnivore</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="protein-button-container">
            <button onClick={calculateProtein}>Calculate</button>
            <button onClick={resetCalculator} className="reset-button">
              Reset
            </button>
          </div>
        </div>
        {/* Results */}
        {results && (
          <div className="results-container">
            <h2>Results</h2>
            <p>
              <strong>Daily Protein Requirement:</strong> {results.dailyProtein}{" "}
              g
            </p>
            {timing === "post" && (
              <p>
                <strong>Post-Workout Protein:</strong>{" "}
                {results.postWorkoutProtein} g
              </p>
            )}
            <p>
              <strong>Recommended Protein Sources:</strong>{" "}
              {results.recommendations.join(", ")}
            </p>
            <p>
              <strong>Protein Timing:</strong> Consume 20–30 g of protein within
              30 minutes post-workout for optimal muscle repair and growth.
            </p>
          </div>
        )}
      </div>
      <ProteinContent />
      <ProteinFAQ />
    </div>
  );
};
export default AdvancedProteinIntakeCalculator;
